import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Calls `onIdle` after `timeoutMs` of no user activity.
 * Resets the timer on any mouse/keyboard/touch event.
 */
export function useIdleTimeout(onIdle: () => void, timeoutMs: number, enabled = true) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onIdleRef.current(), timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    reset();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, reset, { passive: true }));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, reset));
    };
  }, [enabled, reset]);
}

/**
 * Screens that run their own auth or PIN flow, or render for print — the idle
 * lock has no business arming on any of them. Everything else gets the lock,
 * including the pages that are not rendered by Home (/admin, /change-password):
 * the guard used to require a selected organisation in context, which those
 * pages never populate, so the timer silently never armed there.
 */
export const NO_IDLE_LOCK = ['/login', '/select-org', '/setup-pin', '/staff-select', '/system-admin', '/pdf-report'];

/** Whether the idle lock should be armed on this route. */
export function idleLockApplies(pathname: string): boolean {
  return !NO_IDLE_LOCK.some(r => pathname === r || pathname.startsWith(r + '/'));
}
